import React, { useState } from 'react';
import { Box, Typography, Button, Rating, TextField, Snackbar, Alert } from '@mui/material';
import googleIcon from '../../../../assets/images/google.png';
import trustIcon from '../../../../assets/images/trustpilot.png';
import { useHandleSubmit } from './hooks';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import CelebrationOutlinedIcon from '@mui/icons-material/CelebrationOutlined';
import { useLocation } from 'react-router-dom';
import { surveyPortalMixpanel } from '../../../../vendor/mixpanel';

const PaymentPending = () => {
  const [rating, setRating] = useState<number | undefined>(undefined);
  const [ratingSubmitted, setRatingSubmitted] = useState<boolean>(false);
  const handleSubmit = useHandleSubmit();
  const [comment, setComment] = useState('');
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const SEID = queryParams.get('SEID') ?? queryParams.get('seid') ?? '';
  const email = queryParams.get('email') ?? queryParams.get('email_address') ?? '';
  const hasReferral = queryParams.get('has_referral') ?? false;
  const referralAmount = queryParams.get('referral_comp') ?? 0;
  const template = queryParams.get('survey_template') ?? 'SURVEY_2025';
  const isConsultations = template === 'CONSULTATIONS_2025';
  const path = window.location.href;
  const domain = path.split('/surveys/')[0];
  const referralLink = domain + '/referrals/v/' + SEID;
  const surveyId = queryParams.get('survey_id');
  const source = queryParams.get('survey_host');
  const onSubmitRating = () => {
    const feedbackData = { rating: rating ?? 1, respondent_id: SEID, comment: comment };
    handleSubmit(feedbackData);
    setRatingSubmitted(true);
  };
  const hasTrackedRef = React.useRef(false);
  React.useEffect(() => {
    if (!hasTrackedRef.current && SEID) {
      surveyPortalMixpanel.track('Getting Paid Page Viewed', {
        SSEID: SEID,
        survey_id: surveyId,
        survey_host: source,
      });
      if (hasReferral) {
        surveyPortalMixpanel.track('Referral Prompt Viewed', {
          SEID: SEID,
          project_id: surveyId,
          incentive_amount: referralAmount,
          referral_enabled: 'true',
        });
      }
      hasTrackedRef.current = true;
    }
  }, [SEID, hasReferral, referralAmount]);

  const hasTrackedRefRating = React.useRef(false);
  React.useEffect(() => {
    if (!hasTrackedRefRating.current && rating && rating >= 4) {
      surveyPortalMixpanel.track('Leave Public Review Section Shown', {
        SSEID: SEID,
        survey_id: surveyId,
        survey_host: source,
        source: 'Survey',
        rating_given: rating,
        button_location: "You're Getting Paid page",
      });
      hasTrackedRefRating.current = true;
    }
  }, [rating]);
  const trackGoogleReview = () => {
    surveyPortalMixpanel.track('Review on Google Button Clicked', {
      SSEID: SEID,
      survey_id: surveyId,
      survey_host: source,
      source: 'Survey',
      rating_given: rating,
      button_location: "You're Getting Paid page",
    });
  };
  const trackTrustPilotReview = () => {
    surveyPortalMixpanel.track('Review on Trustpilot Button Clicked', {
      SSEID: SEID,
      survey_id: surveyId,
      survey_host: source,
      source: 'Survey',
      rating_given: rating,
      button_location: "You're Getting Paid page",
    });
  };

  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const handleShareClick = () => {
    navigator.clipboard
      .writeText(referralLink)
      .then(() => {
        setSnackbarOpen(true);
        surveyPortalMixpanel.track('Referral Link Copied', {
          SEID: SEID,
          project_id: surveyId,
          incentive_amount: referralAmount,
          referral_link: referralLink,
        });
      })
      .catch((err) => {
        console.error('Failed to copy referral link:', err);
      });
  };

  return (
    <Box maxWidth="480px">
      {hasReferral && (
        <Box
          sx={{
            backgroundColor: 'rgba(231, 237, 255, 1)',
            borderRadius: '16px',
            padding: '20px',
            width: '100%',
            textAlign: 'justify',
            display: 'flex',
            flexDirection: 'row',
            gap: '8px',
          }}
        >
          <Box>
            <PaidOutlinedIcon />
          </Box>
          <Box sx={{ textAlign: 'left' }}>
            <Typography variant="subtitle1" fontWeight="500" fontSize={'16px'} gutterBottom>
              Know other professionals who qualify for this survey?
            </Typography>
            <Typography variant="subtitle1" fontWeight="400" fontSize={'14px'} gutterBottom>
              Earn <span style={{ fontWeight: 700 }}>${referralAmount}</span> for each professional
              who joins NewtonX and completes their first project.
            </Typography>
            <Button variant="contained" sx={{ marginTop: '8px' }} onClick={handleShareClick}>
              Share Link
            </Button>
          </Box>
        </Box>
      )}
      <Box
        sx={{
          background: 'white',
          borderRadius: '16px',
          boxShadow:
            ' 0px 5px 12px 4px rgba(0, 0, 0, 0.09), 0px 3px 6px 0px rgba(0, 0, 0, 0.12),  0px 1px 2px -2px rgba(0, 0, 0, 0.16)',
          mx: 'auto',
          mt: 2,
          px: 2,
          py: 2,
          textAlign: 'center',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
          <Box
            sx={{
              width: '60px',
              height: '60px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '30px',
              background: 'rgba(230, 245, 235, 1)',
              border: '1px solid rgba(195, 229, 205, 1)',
            }}
          >
            <CelebrationOutlinedIcon
              sx={{ color: 'rgba(42, 158, 86, 1)', width: '30px', height: '30px' }}
            />
          </Box>
        </Box>
        <Typography variant={'h2'} marginBottom="16px" fontWeight={700}>
          You’re getting paid!
        </Typography>
        <Typography variant={'body1'} color={'text.secondary'} marginBottom="16px">
          You’ll receive a link via email to claim your reward{' '}
          {isConsultations
            ? 'within 5 business days. The link will be sent to:'
            : 'from our payment partner, Tremendous. The email was sent to:'}
        </Typography>
        <Typography variant={'body1'} color={'text.secondary'} marginBottom="16px" fontWeight={700}>
          {email}
        </Typography>
        <Box
          sx={{
            mx: 'auto',
            mt: 2,
            px: 2,
            py: 2,
            borderRadius: '16px',
            border: '1px solid rgba(228, 231, 235, 1)',
          }}
        >
          {!ratingSubmitted && (
            <>
              <Typography variant={'h3'} marginBottom="16px" fontWeight={700}>
                How was your experience?
              </Typography>
              <Typography variant={'body1'} color={'text.secondary'} marginBottom="16px">
                We use this feedback to create a better survey experience.
              </Typography>
              <Rating
                name="rating-value"
                value={rating}
                onChange={(_, newValue) => {
                  if (newValue && newValue >= 1) {
                    setRating(newValue);
                  }
                }}
                size="large"
                sx={{
                  width: '100%',
                  fontSize: '2.8rem', // increase star size
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '24px',
                  marginLeft: '14px',
                }}
              />

              {rating && rating < 4 && (
                <>
                  <TextField
                    sx={{
                      marginBottom: '4px',
                    }}
                    label="Feedback (optional)"
                    multiline
                    minRows={4}
                    value={comment}
                    fullWidth
                    variant="outlined"
                    inputProps={{ maxLength: 500 }}
                    onChange={(e) => setComment(e.target.value)}
                  />
                  <Typography
                    variant="caption"
                    color={comment.length >= 500 ? 'error' : 'text.secondary'}
                    sx={{ marginBottom: '16px', display: 'block', textAlign: 'right' }}
                  >
                    {comment.length}/500 characters
                  </Typography>
                </>
              )}
              {rating && (
                <Button color="primary" fullWidth variant="outlined" onClick={onSubmitRating}>
                  SUBMIT
                </Button>
              )}
            </>
          )}
          {ratingSubmitted && rating && (
            <>
              {rating < 4 && (
                <Box>
                  <Typography variant={'h3'} marginBottom="16px" fontWeight={700}>
                    Feedback submitted
                  </Typography>
                  <Typography variant={'body1'} color={'text.secondary'} marginBottom="16px">
                    Thank you! We value your feedback and will use it to create a better survey
                    experience.
                  </Typography>
                </Box>
              )}
              {rating >= 4 && (
                <Box>
                  <Typography variant={'h3'} marginBottom="16px" fontWeight={700}>
                    Thanks for your rating!
                  </Typography>
                  <Typography variant={'body1'} color={'text.secondary'} marginBottom="16px">
                    Let others know about your experience with NewtonX by leaving us a public
                    review.
                  </Typography>
                  <Button
                    color="primary"
                    fullWidth
                    variant="outlined"
                    sx={{ mb: '16px' }}
                    component="a"
                    href="https://g.page/r/CX_SiHIANKOMEAE/review"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={trackGoogleReview}
                  >
                    <Box
                      component={'img'}
                      sx={{
                        width: '24px',
                        height: '24px',
                        marginRight: '8px',
                      }}
                      alt="google"
                      src={googleIcon}
                    ></Box>
                    REVIEW ON GOOGLE
                  </Button>
                  <Button
                    color="primary"
                    fullWidth
                    variant="outlined"
                    sx={{ mb: '16px' }}
                    component="a"
                    href="https://www.trustpilot.com/evaluate/newtonx.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={trackTrustPilotReview}
                  >
                    <Box
                      component={'img'}
                      sx={{
                        width: '24px',
                        height: '23px',
                        marginRight: '8px',
                      }}
                      alt="trustpilot"
                      src={trustIcon}
                    ></Box>
                    REVIEW ON TRUSTPILOT
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>
        <Typography
          variant={'body2'}
          fontWeight={700}
          color={'text.secondary'}
          sx={{ marginTop: '24px' }}
        >
          Questions?
        </Typography>
        <Typography variant={'body2'} color={'text.secondary'} sx={{ marginBottom: '16px' }}>
          Contact: <span style={{ color: 'rgba(59, 130, 246, 1)' }}>Payments@newtonx.com</span>
        </Typography>
      </Box>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          sx={{
            backgroundColor: 'green',
            color: 'white',
            fontWeight: 500,
            borderRadius: '8px',
            width: 'fit-content',
          }}
        >
          Referral link copied to clipboard
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PaymentPending;
